import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useApiQuery } from '../hooks/useApiQuery';
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';

interface ContentBlock {
  id: string;
  heading: string;
  body: string;
}

interface TemplateData {
  title: string;
  caption: string;
  description: string;
  createdAt: Date;
  blocks: ContentBlock[];
}

interface TemplateResponse {
  success: boolean;
  message: string;
  data: TemplateData[];
}

const renderFormattedText = (text: string) => {
  return text.split('\n').map((line, lineIndex) => {
    const segments = line.split(/(<b>.*?<\/b>)/g);

    return (
      <React.Fragment key={`line-${lineIndex}`}>
        {segments.map((segment, segmentIndex) => {
          const boldMatch = segment.match(/^<b>(.*?)<\/b>$/);

          if (boldMatch) {
            return (
              <strong key={`segment-${lineIndex}-${segmentIndex}`}>
                {boldMatch[1]}
              </strong>
            );
          }

          return (
            <React.Fragment key={`segment-${lineIndex}-${segmentIndex}`}>
              {segment}
            </React.Fragment>
          );
        })}
        {lineIndex < text.split('\n').length - 1 && <br />}
      </React.Fragment>
    );
  });
};

// Map route paths to template types
const pathToTemplateType: Record<string, string> = {
  '/faq': 'FAQ',
  '/privacy-policy': 'PRIVACY_POLICY',
  '/terms-website': 'TERMS_WEBSITE',
  '/terms-platform': 'TERMS_PLATFORM'
};

const TemplatePage: React.FC = () => {
  const {pathname} = useLocation()
  
  const templateType = pathToTemplateType[pathname || ''] || 'FAQ';
  console.log(pathToTemplateType[pathname]);
  
  const { data: response, isLoading, error } = useApiQuery<TemplateResponse>({
    queryKey: ['template', templateType],
    request: {
      method: 'GET',
      url: `/admin/templates/type/${templateType.toUpperCase()}`
    }
  });

  const templateData = response?.data?.[0];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          <span className="text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !response?.success) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Content Not Available</h1>
          <p className="text-gray-600">
            {error instanceof Error ? error.message : 'Failed to load content. Please try again later.'}
          </p>
        </div>
      </div>
    );
  }

  if (!templateData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">No Content Available</h1>
          <p className="text-gray-600">This page doesn't have any content yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-sm p-8">
          {/* Title Section */}
          <div className="text-left mb-8">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">{templateData.title}</h1>
            <p className="font-semibold text-xs text-gray-500 pb-3">Effective: {format(templateData.createdAt , 'MMMM d, yyyy')}</p>
            {templateData.caption && (
              <p className="text-lg text-gray-600 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                {renderFormattedText(templateData.caption)}
              </p>
            )}
          </div>

          {/* Description */}
          {templateData.description && (
            <div className="mb-8">
              <p className="text-gray-800 leading-relaxed">
                {renderFormattedText(templateData.description)}
              </p>
            </div>
          )}

          {/* Content Blocks */}
          {templateData.blocks && templateData.blocks.length > 0 && (
            <div className="space-y-6">
              {templateData.blocks.map((block) => (
                <div key={block.id} className="border-b border-gray-200 pb-6 last:border-b-0">
                  {block.heading && (
                    <h2 className="text-xl font-semibold text-slate-900 mb-3 border-l-4 border-red-500 bg-red-50 pl-6 py-2.5">
                      {block.heading}
                    </h2>
                  )}
                  {block.body && (
                    <div className="text-gray-700 leading-relaxed pl-6">
                      {renderFormattedText(block.body)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplatePage;
